import React, { useEffect, useRef, useMemo } from 'react';

import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';

import { dateFormat, timeFormat, convertLicenseplate } from '../../../lib/utils/Utils';

import useUnplannedRoute from '../../../hooks/useUnplannedRoute';
import useRouteCoordinates from '../../../hooks/useRouteCoordinate';

import Page from '../../../components/page/Page';
import ErrorState from '../../../components/state/ErrorState';
import LoadingState from '../../../components/state/LoadingState';
import Map from '../../../components/maps/Map';
import Marker from '../../../components/maps/Marker';
import { stringToColor } from '../../../lib/utils/stringColor';

import Vehicle from './vehicle/Vehicle';
import GpsgatePathUnplanned from './gpsgate/GpsgatePathUnplanned';
import GpsgateCoordinatesUnplanned from './gpsgate/GpsgateCoordinatesUnplanned';
import Stops from './stops/Stops';
import StopList from './stops/StopList';

import './Details.css';

const DetailsUnplanned = ({ history, match: { params } }) => {
    const { branche_id, route_id } = params;
    const { search } = useLocation();
    const ref = new URLSearchParams(search).get('ref') || '';

    const mapRef = useRef(null);

    const { route, error, loading, fetchRoute } = useUnplannedRoute();
    const { coordinates = [], fetchCoordinates } = useRouteCoordinates();

    const fitBoundsRef = useRef(false);

    //const user = useSelector((state) => state.user.user);
    const vehicle = useSelector((state) => state.vehicles.data[convertLicenseplate(route?.licenseplate)]);

    useEffect(() => {
        if (route_id) {
            fetchRoute(route_id);
            fetchCoordinates(route_id);
        }
    }, [ route_id, fetchRoute, fetchCoordinates ]);

    const { routes, segments } = useMemo(() => {

        const routes = [];

        const segments = {}

        let lastKey;

        let driverIndex = 0;

        if (coordinates !== null) {
            coordinates.forEach((coordinate, index) => {
                const { driver, planned_route_id, latitude, longitude } = coordinate;

                if (driver === '') { return }

                const driverKey = driver !== '' ? driver : 'unknown';
                const plannedRouteKey = planned_route_id !== null ? planned_route_id : 'unplanned';

                if (lastKey !== undefined && lastKey !== `${driverIndex}-${driverKey}-${plannedRouteKey}`) {
                    segments[lastKey].endTime = coordinates[index - 1].updatedTime;
                    driverIndex++;
                }

                const key = `${driverIndex}-${driverKey}-${plannedRouteKey}`;

                if (segments[key] === undefined) {
                    segments[key] = {
                        key: key,
                        driver: driverKey,
                        driverColor: planned_route_id !== null ? '#57004e' : (driver ? stringToColor(driver, { saturation: 90, lightness: 50 }) : '#bdbdbd'),
                        planned_route_id: planned_route_id,
                        startTime: coordinate.updatedTime,
                        endTime: null,
                        coordinates: []
                    };
                } 

                segments[key].coordinates.push(coordinate);

                // Add end time if last coordinate
                if (coordinates.length === index + 1) {
                    segments[key].endTime = coordinate.updatedTime;
                }

                lastKey = key;
                
                if (planned_route_id !== null && !routes.includes(planned_route_id)) {
                    routes.push(planned_route_id);
                }  
            });
        }

        return {
            routes,
            segments,
      
        }
        
    }, [ coordinates ]);

    useEffect(() => {

        if (!fitBoundsRef.current && mapRef.current?.getMap() && coordinates?.length > 0) {
            console.log('fit bounds')
            let bounds = new window.google.maps.LatLngBounds();
            coordinates.forEach((coordinate, index) => {
                bounds.extend(new window.google.maps.LatLng(coordinate.latitude, coordinate.longitude));
            });

            mapRef.current.getMap().fitBounds(bounds);
            mapRef.current.getMap().setCenter(bounds.getCenter());
            mapRef.current.getMap().panBy(-200, 0);

            fitBoundsRef.current = true;
        }
    }, [ mapRef, coordinates, ref ]);

    const onBack = () => {
        history.goBack();
    }

    const renderInfo = () => {

        const { createdAt, licenseplate  } = route;

        return (
            <>
            <div className="card">
                <div className="route_info_header">
                    <h2 style={ {margin:0, padding:20} }>Route informatie</h2>
                </div>

                <div className="route_info">
                    {
                        (licenseplate)
                        &&
                        <div className="line">
                            <div className="label">Kenteken: </div>
                            <div className="value">{ licenseplate }</div>
                        </div>
                    }
                    <div className="line">
                        <div className="label">Aangemaakt op: </div>
                        <div className="value">{ dateFormat(new Date(createdAt), false) } { timeFormat(new Date(createdAt)) }</div>
                    </div>

                    <div className="line">
                        <div className="label">GPS puntent: </div>
                        <div className="value">{ coordinates?.length ? coordinates.length : 0 }</div>
                    </div>
   
                </div>
            </div>
            
            {
                routes.length > 0
                &&
                <div className="card">
                    <div className="route_info_header">
                        <h2 style={ {margin:0, padding:20} }>Routes</h2>
                    </div>

                    <div className="route_info">
                        {
                            routes
                            &&
                            routes.map((route) => {
                                return (
                                    <div className="line">
                                        <div className="label">Route: </div>
                                        <div className="value">{ route }</div>
                                    </div>
                                )
                            })
                        }
                    
                    </div>
                </div>
            }

            <div className="card">
                <div className="route_info_header">
                    <h2 style={ {margin:0, padding:20} }>Chauffeurs</h2>
                </div>

                <div className="route_info">
                    {
                        segments
                        &&
                        Object.values(segments).map(({ key, driver, startTime, endTime, driverColor }, index) => {
                            return (
                        
                                <div key={key+index} className="line">
                                    {
                                        driver !== 'unknown'
                                        ?
                                        <div className="value" style={{ color: driverColor }}>{ driver }</div>
                                        :
                                        <div className="value" style={{ color: '#bdbdbd' }}>Onbekend</div>
                                    }
                                    <div >{ timeFormat(new Date(startTime)) } { endTime !== null ? `- ${timeFormat(new Date(endTime))}` : '- No time' }</div>
                                </div>
                                
                            )
                        })
                    }
                </div>

                
            </div>
            </>
        );
    }

    const renderStops = () => {

        const { stops } = route;

        return (
            <div className="card" style={ {display:'flex', flexDirection:'column', flex:1, overflow:'hidden'} }>
                <div className="route_info_header">
                    <h2 style={ {margin:0, padding:20} }>Stops</h2>
                </div>
                <div className="scroll-y"  >
                    <StopList stops={stops} getMap={() => mapRef.current} activities={[]} />
                </div>
                
            </div>
        );
    }

    const renderMap = () => {
        const { licenseplate, branche, stops } = route;

        return (
            <Map
                ref={mapRef}
                center={{
                    latitude: branche?.latitude,
                    longitude: branche?.longitude
                }}
                zoom={10}
                //panX={-200}
                streetViewControl={true}
            >
                {
                    branche !== null
                    &&
                    <Marker
                        key={route_id}
                        position={{
                            latitude: branche?.latitude,
                            longitude: branche?.longitude
                        }}
                        icon={{
                            path: 'M14.4 6L14 4H5v17h2v-7h5.6l.4 2h7V6z',
                            fillColor: '#57004e'
                        }}
                        infoWindowContent={(
                            <div className='infowindow'><span className='name'>{ branche?.name }</span></div>
                        )}
                    />
                }

                {
                    ref !== 'archive'
                    &&
                    <Vehicle licenseplate={licenseplate} type={(branche?.truck ? branche.truck : 'normal')} />
                }

                <Stops stops={stops} activities={[]} />

                <GpsgatePathUnplanned route_id={route_id} coordinates={coordinates} segments={segments} />

                {
                    
                    Array.isArray(coordinates)
                    &&
                    <GpsgateCoordinatesUnplanned coordinates={coordinates} />
                }
      
            </Map>
        );
    }
    
    if (route === null) {
        if (loading) { return <LoadingState label="Bezig met laden..." />; }
        if (error) { return <ErrorState label="Er is een fout opgetreden" message={error.message} />; }
        return null;
    }

    return (
        <Page onBack={onBack}>
            <div style={ {position: 'relative', height:'100%'} }>
                
                <div className="sidebar">
                    { renderInfo() }
                    { renderStops() }
                </div>
                
                { renderMap() }
                
            </div>
        </Page>
    );
}

export default DetailsUnplanned;
